'use client'

import { useEffect, useState } from "react"
import { Button, Text } from "@mantine/core"
import { useViewportSize } from "@mantine/hooks"
import Grudder from "@grudder/components/Common/SvgComponent/Grudder"
// import LottiePlayer from "@bytec.in/lottie-player"

interface MobileViewProviderProps {
  children: React.ReactNode
}

export default function MobileViewProvider({ children }: MobileViewProviderProps) {
  const { width } = useViewportSize()
  const [isMobile, setIsMobile] = useState(false)
  const [visible, setVisible] = useState(false)

  // threshold for small devices
  useEffect(() => {
    setIsMobile(width < 600)
  }, [width])

  // animation delay
  useEffect(() => {
    if (isMobile) {
      const timer = setTimeout(() => setVisible(true), 200)
      return () => clearTimeout(timer)
    }
  }, [isMobile])

  if (!isMobile) return children

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center h-screen w-full bg-gradient-to-br from-gr-primary-50 via-white to-gr-gray-100 px-4">
      <div
        className={`bg-gr-gray-0 rounded-2xl shadow-lg border border-gr-gray-100 p-8 max-w-sm w-full text-center transition-all duration-500 ease-out transform ${
          visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
        }`}
      >
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Grudder height={110} width={200} />
        </div>

        {/* Optional small Lottie animation (if available) */}
        {/* <div className="flex justify-center mb-4">
          <LottiePlayer autoplay loop src="/animations/mobile-guide.json" style={{ width: 120, height: 120 }} />
        </div> */}

        {/* Title */}
        <Text size="xl" fw={700} className="text-gr-gray-900 mb-2 tracking-tight">
          Use Our Mobile App 📱
        </Text>

        {/* Description */}
        <Text size="sm" className="text-gr-gray-600 mb-6 leading-relaxed">
          For the best experience, download our GrowthRudder app from the store.
        </Text>

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <Button
            component="a"
            href="https://play.google.com/store/apps/details?id=com.growthrudder"
            fullWidth
            radius="md"
            size="md"
            className="bg-gr-primary-600 hover:bg-gr-primary-700 text-gr-gray-0 transition-colors"
          >
            Get it on Google Play
          </Button>

          <Button
            component="a"
            href="https://apps.apple.com/app/id0000000"
            fullWidth
            radius="md"
            size="md"
            variant="outline"
            className="border border-gr-gray-200 text-gr-gray-800 hover:bg-gr-gray-100 transition-colors"
          >
            Download on App Store
          </Button>
        </div>

        {/* Support link */}
        <Text size="xs" className="text-gr-gray-500 mt-6">
          Need help?{" "}
          <a href="mailto:support@growthrudder.com" className="text-gr-primary-600 hover:text-gr-primary-700 underline-offset-2 hover:underline">
            Contact Support
          </a>
        </Text>
      </div>
    </div>
  )
}
